/**
 * This is a configurable form element which can be displayed
 * at the top of the customer consent form which will control
 * the terms & conditions intro text and the list of opt-ins
 * the customer has consented to.
 */
import React from "react";
import cn from "clsx";
import { get } from "../../utils/helpers";
import { RenderElementProps } from "../ConfigurableForm/FormElement";
import "./CustomerConsentIntro.css";

export interface CustomerConsentIntroProps extends RenderElementProps {
  showCurrentDate: boolean;
}

export default function CustomerConsentIntro({
  id,
  className,
  display = true,
  showCurrentDate,
  formProps
}: CustomerConsentIntroProps) {
  const { sharedState, t } = formProps;
  const appZone = get(sharedState, "appState.zone");
  const postConsent = get(sharedState, "formState.postConsent");
  const emailConsent = get(sharedState, "formState.emailConsent");
  const phoneConsent = get(sharedState, "formState.phoneConsent");
  const appsConsent = get(sharedState, "formState.appsConsent");
  const consented = [
    postConsent && "post",
    emailConsent && "email",
    phoneConsent && "phone",
    appsConsent && "apps"
  ].filter(x => x);
  const today = new Date();
  const month = today.getMonth() + 1;
  const currentDateStr =
    month + "/" + today.getDate() + "/" + today.getFullYear();
  return (
    <div
      id={id}
      className={cn("customer-consent-intro", className)}
      aria-hidden={!display}
    >
      {!!consented.length && (
        <>
          <div className="section-text agreed-fields">
            {/* Attempt to use translation key for specific zone, otherwise use generic translation key */}
            <p>
              {t({
                translationId: [
                  `customer.consent.sections.termsAndConditions.agreedFields.${appZone}`,
                  "customer.consent.sections.termsAndConditions.agreedFields"
                ]
              })}
            </p>
          </div>
          <ul className="communication-methods-consented">
            {consented.map(consentedMethod => (
              <li
                key={consentedMethod}
                className={cn(
                  "communication-method-consented",
                  `communication-method-${consentedMethod}`
                )}
              >
                {t(`customer.consent.communicationMethod.${consentedMethod}`)}
              </li>
            ))}
          </ul>
        </>
      )}
      <div className="section-text">
        {/* Attempt to use translation key for specific zone, otherwise use generic translation key */}
        <p>
          {t({
            translationId: [
              `customer.consent.sections.termsAndConditions.termsIntro.${appZone}`,
              "customer.consent.sections.termsAndConditions.termsIntro"
            ]
          })}
        </p>
      </div>
      {showCurrentDate && (
        <div className="section-text">
          {t("customer.consent.date")}
          <br />
          {currentDateStr}
          <br />
          <br />
        </div>
      )}
    </div>
  );
}

/**
 * Special flag to ensure that this component gets extra `formProps`
 * property passed through when rendering.
 */
CustomerConsentIntro.renderWithFormProps = true;
